import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { PAGE_TITLE_MAPPING } from '../config'
import { EntityType } from '@prisma/client'
import { UserDTO } from '@/types/dtos/user-dto'
import { User } from 'next-auth'
import { format } from 'date-fns'
import { DefaultPriceDTO } from '@/types/dtos/defaultPrice-dto'
import crypto from 'crypto'
import { convertCurrency, formatCurrency, ExchangeRates } from './currency-converter'
import { BookingDTO } from '@/types/dtos/booking-dto'
import { PropertyDTO } from '@/types/dtos/property-dto'
import countries from '@/lib/data/countries.json'
import { z } from 'zod'
import type { UploadKind } from '@/lib/storage/keys'

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

export function getProviderDisplayName(provider: string): string {
    switch (provider.toLowerCase()) {
        case 'google':
            return 'Google'
        case 'microsoft-entra-id':
            return 'Microsoft'
        case 'resend':
            return 'Magic Link'
        default:
            return provider
    }
}

/**
 * Upload a public image to the S3-compatible bucket via our server-side upload
 * route (the file is streamed through the app to the private Railway bucket).
 * Returns the stored media ref (`/api/media/<key>`, persisted as-is in e.g.
 * Media.path / User.image / Company.logoImage) or `null` on failure.
 *
 * `kind` selects the bucket prefix: 'avatar' for profile/company images,
 * 'listing' (default) for property/room/office gallery photos.
 */
export const uploadFile = async (
    file: File,
    setError: (message: string) => void,
    kind: UploadKind = 'listing'
): Promise<string | null> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('kind', kind)
    const response = await fetch('/api/avatar', {
        method: 'POST',
        body: formData
    })
    if (!response.ok) {
        setError('Upload failed')
        return null
    }
    const data = await response.json()
    return (data.url as string) ?? null
}

/**
 * Resolve an image field value for DB persistence. Uploads a pending `File` when
 * present; otherwise uploads when the value is a cropped `data:` preview so we
 * never send large data URLs through server actions.
 */
export const resolveImageForPersist = async (
    imageValue: string,
    pendingFile: File | undefined,
    setError: (message: string) => void,
    kind: UploadKind = 'avatar'
): Promise<string> => {
    if (pendingFile) {
        const uploadedUrl = await uploadFile(pendingFile, setError, kind)
        if (!uploadedUrl) {
            throw new Error('Image upload failed')
        }
        return uploadedUrl
    }

    if (imageValue.startsWith('data:')) {
        const blob = await fetch(imageValue).then((res) => res.blob())
        const extension = blob.type === 'image/jpeg' ? '.jpg' : blob.type === 'image/webp' ? '.webp' : '.png'
        const file = new File([blob], `upload${extension}`, { type: blob.type || 'image/png' })
        const uploadedUrl = await uploadFile(file, setError, kind)
        if (!uploadedUrl) {
            throw new Error('Image upload failed')
        }
        return uploadedUrl
    }

    return imageValue
}

export function getCurrentYear() {
    return new Date().getFullYear()
}

export function getCurrentPageTitle(page: string): string {
    return PAGE_TITLE_MAPPING[page as keyof typeof PAGE_TITLE_MAPPING] || ''
}

/**
 * Minimal user info type for formatter functions
 */
type UserLike = {
    name?: string | null
    firstName?: string | null
    lastName?: string | null
    email?: string | null
}

export function formatterUserAvatarName(user: UserDTO | User | UserLike | undefined) {
    if (!user) return ''
    return user.firstName && user.lastName
        ? `${user.firstName[0]}${user.lastName[0]}`
        : user.email?.[0]?.toUpperCase() || ''
}

export function formatterUserLabel(user: UserDTO | User | UserLike | undefined) {
    if (!user) return ''
    if (user.name) return user.name
    if (user.firstName || user.lastName) return user.firstName + ' ' + user.lastName
    if (user.email) return user.email
}

export function formatterDate(date: Date | null | undefined): string {
    if (!date) return ''
    return format(new Date(date), 'MMMM do, yyyy, h:mm a')
}

export function formatterValueUnit(
    value: number | null | undefined | string,
    decimalPlaces: number,
    useThousandSeperator?: boolean,
    unit?: string
): string {
    let returnValue = ''

    //Value based formatting
    if (!value) return returnValue
    if (typeof value === 'string') {
        const numberValue = parseFloat(value)
        if (isNaN(numberValue)) {
            throw new Error('Invalid number provided to roundNumber function')
        }
        returnValue = numberValue.toFixed(decimalPlaces)
    } else {
        returnValue = value.toFixed(decimalPlaces)
    }

    if (useThousandSeperator) {
        const [integerPart, decimalPart] = returnValue.split('.')
        const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        returnValue = decimalPart !== undefined ? `${formattedIntegerPart}.${decimalPart}` : formattedIntegerPart
    }

    //Unit based formatting
    if (unit) {
        returnValue = `${unit} ${returnValue}`
    }

    return returnValue
}

export function getAvailabilityDates(from: Date | null, to: Date | null) {
    const availableFrom = from
        ? new Date(from.getFullYear(), from.getMonth(), from.getDate(), 0, 0, 0, 0).toISOString()
        : new Date().toISOString()

    const availableTo = to
        ? new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999).toISOString()
        : new Date().toISOString()

    return { availableFrom, availableTo }
}

export function getTimeFormat(time: string | undefined) {
    return time ? `1970-01-01T${time}:00.000Z` : new Date().toISOString()
}

export function deepClone(entity: any): any {
    if (entity === null || entity === undefined) {
        return entity
    }

    // Handle Prisma Decimal
    if (
        typeof entity === 'object' &&
        's' in entity &&
        'e' in entity &&
        'd' in entity &&
        typeof entity.toNumber === 'function'
    ) {
        return entity.toNumber()
    }

    // Handle Date
    if (entity instanceof Date) {
        return new Date(entity.getTime())
    }

    // Handle Array
    if (Array.isArray(entity)) {
        return entity.map(deepClone)
    }

    // Handle plain objects
    if (typeof entity === 'object') {
        const cloned: any = {}
        for (const key in entity) {
            if (Object.prototype.hasOwnProperty.call(entity, key)) {
                cloned[key] = deepClone(entity[key])
            }
        }
        return cloned
    }

    // Return primitives
    return entity
}

export function translateNewLineToHtmlBreak(str: string) {
    return str.replace(/\n/g, '<br />')
}

export function getCalendarWeek(date: Date = new Date()): number {
    // Set the start of the week to Monday

    //make a copy of the date
    const copyDate = new Date(date.getTime())
    const dayOfWeek = copyDate.getUTCDay() || 7 // Make Sunday (0) return 7
    copyDate.setUTCDate(copyDate.getUTCDate() + 4 - dayOfWeek) // Adjust to the nearest Thursday

    // Calculate the first day of the year
    const startOfYear = new Date(Date.UTC(copyDate.getUTCFullYear(), 0, 1))

    // Calculate the calendar week
    const weekNumber = Math.ceil(((copyDate.getTime() - startOfYear.getTime()) / 86400000 + 1) / 7)

    return weekNumber
}

export function entityTypeAndIdToUrl(entityType?: string, entityId?: string) {
    if (!entityType || !entityId) {
        return null
    }
    switch (entityType) {
        case EntityType.Company:
            return '/company-settings'
        case EntityType.Property:
            return `/properties/${entityId}`
    }
    return null
}

export function getAverageDefaultPrice(defaultPrice: DefaultPriceDTO | null) {
    if (!defaultPrice) return null
    const sum =
        parseFloat(defaultPrice.monday.price.amount.toString()) +
        parseFloat(defaultPrice.tuesday.price.amount.toString()) +
        parseFloat(defaultPrice.wednesday.price.amount.toString()) +
        parseFloat(defaultPrice.thursday.price.amount.toString()) +
        parseFloat(defaultPrice.friday.price.amount.toString()) +
        parseFloat(defaultPrice.saturday.price.amount.toString()) +
        parseFloat(defaultPrice.sunday.price.amount.toString())
    const average = sum / 7
    return average.toFixed(2)
}

export function getPlainText(html: string) {
    return html && html.length > 0 ? html.replace(/<[^>]*>/g, '').trim() : ''
}

export function extractHostIdFromUrl(url: string): string | null {
    const regex = /\/users\/(?:show|profile)\/(\d+)/ // Match "/users/{show|profile}/{id}" in the URL
    const match = url.match(regex)
    return match ? match[1] : null
}

export function generatePassword(email: string, length = 14): string {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz'
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const numbers = '0123456789'
    const special = '!%?-;:.+&_*'

    if (length < 12) {
        length = 12
    }
    const emailLower = email.toLowerCase()
    const availableChars = (lowercase + uppercase + numbers + special)
        .split('')
        .filter((char) => !emailLower.includes(char))
        .join('')

    if (availableChars.length < length) {
        throw new Error('Not enough chars')
    }
    const passwordArray: string[] = []
    passwordArray.push(lowercase[Math.floor(Math.random() * lowercase.length)])
    passwordArray.push(uppercase[Math.floor(Math.random() * uppercase.length)])
    passwordArray.push(numbers[Math.floor(Math.random() * numbers.length)])
    passwordArray.push(special[Math.floor(Math.random() * special.length)])
    for (let i = passwordArray.length; i < length; i++) {
        passwordArray.push(availableChars[Math.floor(Math.random() * availableChars.length)])
    }
    return passwordArray.sort(() => Math.random() - 0.5).join('')
}

/**
 * Deterministically derive a Rentals United sub-user password from the host's
 * email, keyed by `ENCRYPTION_KEY`. Unlike `generatePassword` (random), this is
 * STABLE for a given email, so a connect retry after a partial failure
 * reproduces the exact same password. That is what lets us recover a sub-user
 * that already exists at RU (Status 95 "Email already exists") instead of being
 * permanently locked out by a lost random password.
 *
 * Output: 12 chars, guaranteed to contain a lowercase, uppercase, digit and
 * special character (RU password complexity).
 */
export function deriveRuUserPassword(email: string): string {
    const secret = process.env.ENCRYPTION_KEY
    if (!secret) {
        throw new Error('ENCRYPTION_KEY is not set; cannot derive RU user password')
    }
    const lower = 'abcdefghijkmnpqrstuvwxyz'
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
    const digits = '23456789'
    const special = '!@#$%*-_'
    const all = lower + upper + digits + special
    const hash = crypto.createHmac('sha256', secret).update(email.trim().toLowerCase()).digest()
    const chars: string[] = []
    for (let i = 0; i < 12; i++) {
        chars.push(all[hash[i] % all.length])
    }
    // Guarantee each character class at deterministic positions.
    chars[0] = lower[hash[12] % lower.length]
    chars[1] = upper[hash[13] % upper.length]
    chars[2] = digits[hash[14] % digits.length]
    chars[3] = special[hash[15] % special.length]
    return chars.join('')
}

// AES-256-GCM needs a 32-byte key. `ENCRYPTION_KEY` is also consumed as a raw
// HMAC secret by `deriveRuUserPassword`, so we must NOT constrain its format
// there. To keep both usages working with the *same* env value, derive the AES
// key here: a canonical 64-char hex value is used verbatim (back-compat with any
// ciphertext already produced that way), any other value (e.g. base64) is hashed
// to a stable 32-byte key. This avoids ERR_CRYPTO_INVALID_KEYLEN on non-hex keys.
function getEncryptionKey(): Buffer {
    const secret = process.env.ENCRYPTION_KEY
    if (!secret) {
        throw new Error('ENCRYPTION_KEY is not set; cannot encrypt/decrypt')
    }
    if (/^[0-9a-fA-F]{64}$/.test(secret)) {
        return Buffer.from(secret, 'hex')
    }
    return crypto.createHash('sha256').update(secret).digest()
}

export function encrypt(text: string) {
    const KEY = getEncryptionKey()
    const iv = crypto.randomBytes(16)
    const cipher = crypto.createCipheriv('aes-256-gcm', KEY, iv)
    const encrypted = Buffer.concat([cipher.update(text, 'utf8'), cipher.final()])
    const tag = cipher.getAuthTag()
    return iv.toString('hex') + ':' + tag.toString('hex') + ':' + encrypted.toString('hex')
}

export function decrypt(encrypted: string) {
    const KEY = getEncryptionKey()
    const [ivHex, tagHex, dataHex] = encrypted.split(':')
    const iv = Buffer.from(ivHex, 'hex')
    const tag = Buffer.from(tagHex, 'hex')
    const encryptedData = Buffer.from(dataHex, 'hex')
    const decipher = crypto.createDecipheriv('aes-256-gcm', KEY, iv)
    decipher.setAuthTag(tag)
    const decrypted = Buffer.concat([decipher.update(encryptedData), decipher.final()])
    return decrypted.toString('utf8')
}

export function loadScriptSequentially(src: string, attrs: Record<string, string> = {}): Promise<void> {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve()
            return
        }

        const script = document.createElement('script')
        script.src = src
        Object.entries(attrs).forEach(([k, v]) => script.setAttribute(k, v))
        script.onload = () => resolve()
        script.onerror = () => reject(new Error(`Failed to load script ${src}`))
        document.body.appendChild(script)
    })
}

export function formatPriceWithCurrency(amount: number, targetCurrency: string, exchangeRates: ExchangeRates): string {
    const convertedAmount = convertCurrency(amount, targetCurrency, exchangeRates)
    return formatCurrency(convertedAmount, targetCurrency)
}

export function formatPriceFromStoredCurrency(
    amount: number,
    storedCurrency: string,
    targetCurrency: string,
    exchangeRates: ExchangeRates
): string {
    // If stored currency is not EUR, first convert to EUR
    let amountInEur = amount
    if (storedCurrency !== 'EUR') {
        const rateToEur = 1 / (exchangeRates.rates[storedCurrency] || 1)
        amountInEur = amount * rateToEur
    }

    // Then convert from EUR to target currency
    return formatPriceWithCurrency(amountInEur, targetCurrency, exchangeRates)
}

export function calculatePropertyRating(
    bookings: BookingDTO[],
    ratingValue: string,
    withCount: boolean = true
): string {
    const now = new Date()
    let weightedSum = 0
    let totalWeight = 0
    let countReviews = 0

    bookings.forEach((booking) => {
        const review = booking.reviewGuest
        if (!review) return

        let rating: number = NaN
        switch (ratingValue) {
            case 'ratingOverall':
                rating = review.ratingOverall
                break
            case 'ratingLocation':
                rating = review.ratingLocation
                break
            case 'ratingCleaniness':
                rating = review.ratingCleaniness
                break
            case 'ratingCommunication':
                rating = review.ratingCommunication
                break
        }
        if (isNaN(rating)) return

        const createdAt = review.createdAt ? new Date(review.createdAt) : now
        const ageInMonths = (now.getFullYear() - createdAt.getFullYear()) * 12 + (now.getMonth() - createdAt.getMonth())
        const weight = Math.max(0.1, 1 - (ageInMonths / 12) * 0.9)

        weightedSum += rating * weight
        totalWeight += weight
        countReviews++
    })

    if (countReviews === 0 || totalWeight === 0) return 'New'
    const rating = weightedSum / totalWeight
    const formatted = rating.toFixed(2)

    return withCount ? `${formatted} (${countReviews})` : `${formatted}`
}

export function shortenText(text: string | undefined, maxLength: number): string {
    if (!text) return ''
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength).trim() + '…'
}

/** Listing headline for cards and public detail when DB title is missing. */
export function getPropertyListingTitle(property: Pick<PropertyDTO, 'title' | 'location'>): string {
    const trimmed = property.title?.trim()
    if (trimmed) return trimmed
    const city = property.location?.address?.city?.trim()
    const countryCode = property.location?.address?.country
    const countryName = countryCode ? countries.find((c) => c.code === countryCode)?.name : undefined
    const parts = [city, countryName].filter((p): p is string => Boolean(p?.length))
    if (parts.length > 0) return parts.join(', ')
    return 'Property'
}

export const normalizeDate = z.date().transform((d) => {
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
})
